import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Routes, Route, Navigate, useNavigate, Link } from 'react-router-dom';
import './App.css';
import { FloorPlan, FloorPlanItem, Position } from './types';
import FloorPlanCanvas from './components/FloorPlanCanvas';
import Toolbar from './components/Toolbar';
import PropertyPanel from './components/PropertyPanel';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import PrivateRoute from './components/PrivateRoute';
import Home from './components/Home';
import { useAuth } from './contexts/AuthContext';
import { savePlan, updatePlan } from './services/firestore';

// Conversion factor for feet to pixels (1 foot = 10 pixels)
const FEET_TO_PIXELS = 10;
// DPI for PDF generation (standard 72 DPI)
const PDF_DPI = 72;
// One inch border in points (72 points = 1 inch)
const BORDER_SIZE = 72;

// Modern color scheme
const COLORS = {
  primary: '#fbf2ed', // Header color
  secondary: '#d0d7c9', // Button color
  accent: '#d0d7c9', // Accent color (using same as buttons)
  light: '#f8f9fa', // Light background
  dark: '#555555', // Dark text
  lightText: '#555555', // Updated for readability on light backgrounds
  border: '#dee2e6',
  hover: '#c2c9bc', // Darker button color for hover states
  success: '#28a745',
  error: '#dc3545',
  gray: '#6c757d',
  lightGray: '#e9ecef',
};

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  font-family: 'Inter', 'Segoe UI', 'Roboto', sans-serif;
  color: ${COLORS.dark};
  background-color: ${COLORS.light};
`;

const Header = styled.header`
  background-color: ${COLORS.primary};
  color: ${COLORS.lightText};
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DimensionsForm = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 12px 16px;
  border-radius: 8px;
`;

const DimensionInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #d0d7c9;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const SnapCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
  background-color: #d0d7c9;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #555555;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: #333333;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.5px;
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100vh;
  overflow: hidden;
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  background-color: ${COLORS.light};
`;

const CanvasContainer = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 16px;
  background-color: white;
  box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.05);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: ${COLORS.secondary};
  color: #555555;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: ${COLORS.hover};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
`;

const ApplyButton = styled(Button)`
  background-color: ${COLORS.accent};
  
  &:hover {
    background-color: #239d9d;
  }
`;

const Input = styled.input`
  width: 80px;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.7);
  color: #555555;
  text-align: center;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 2px rgba(40, 181, 181, 0.2);
  }

  &::placeholder {
    color: rgba(85, 85, 85, 0.5);
  }
`;

const Checkbox = styled.input`
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(85, 85, 85, 0.3);
  border-radius: 4px;
  margin: 0;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  background-color: rgba(255, 255, 255, 0.7);

  &:checked {
    background-color: ${COLORS.accent};
    border-color: ${COLORS.accent};
  }

  &:checked::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 6px;
    width: 4px;
    height: 8px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  &:hover {
    border-color: rgba(85, 85, 85, 0.5);
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
`;

const UnitLabel = styled.span`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

// Export VenuePlannerApp to use in Home component
export function VenuePlannerApp({ 
  floorPlan: initialFloorPlan, 
  onSavePlan,
  requireAuthForSave = false,
  isLoggedIn = false
}: { 
  floorPlan?: FloorPlan, 
  onSavePlan?: (plan: FloorPlan) => void,
  requireAuthForSave?: boolean,
  isLoggedIn?: boolean 
}) {
  // Initialize with provided plan or default values
  const defaultWidthFeet = 80; // 800px / 10 = 80ft
  const defaultHeightFeet = 60; // 600px / 10 = 60ft
  
  const [floorPlan, setFloorPlan] = useState<FloorPlan>(initialFloorPlan || {
    id: uuidv4(),
    name: 'New Venue',
    size: { 
      width: defaultWidthFeet * FEET_TO_PIXELS, 
      height: defaultHeightFeet * FEET_TO_PIXELS 
    },
    items: []
  });
  
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [dimensionsForm, setDimensionsForm] = useState({
    width: defaultWidthFeet,
    height: defaultHeightFeet
  });
  const [inputValues, setInputValues] = useState({
    width: defaultWidthFeet.toString(),
    height: defaultHeightFeet.toString()
  });
  const [snapToGrid, setSnapToGrid] = useState(false);

  const selectedItem = selectedItemId 
    ? floorPlan.items.find(item => item.id === selectedItemId) || null 
    : null;

  const canvasRef = useRef<HTMLDivElement>(null);

  const handleAddItem = (item: FloorPlanItem) => {
    setFloorPlan(prev => ({
      ...prev,
      items: [...prev.items, item]
    }));
    setSelectedItemId(item.id);
  };

  const handleItemMove = (id: string, position: Position, rotation?: number) => {
    let finalPosition = { ...position };
    
    // If snap to grid is enabled, round to nearest grid cell (1 foot)
    if (snapToGrid) {
      finalPosition = {
        x: Math.round(position.x / FEET_TO_PIXELS) * FEET_TO_PIXELS,
        y: Math.round(position.y / FEET_TO_PIXELS) * FEET_TO_PIXELS
      };
    }
    
    setFloorPlan(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.id === id) {
          let updatedItem = { ...item, position: finalPosition };
          // If rotation was provided, update it too
          if (rotation !== undefined) {
            updatedItem.rotation = rotation;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const handleItemChange = (updatedItem: FloorPlanItem) => {
    setFloorPlan(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === updatedItem.id 
          ? updatedItem
          : item
      )
    }));
  };

  const handleItemDelete = (id: string) => {
    setFloorPlan(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
    setSelectedItemId(null);
  };

  const handleItemSelect = (id: string | null) => {
    setSelectedItemId(id);
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all items?')) {
      setFloorPlan(prev => ({
        ...prev,
        items: []
      }));
      setSelectedItemId(null);
    }
  };

  const handleSave = () => {
    if (onSavePlan) {
      onSavePlan(floorPlan);
    }
  };

  const handleDimensionChange = (dimension: 'width' | 'height', value: string) => {
    setInputValues(prev => ({
      ...prev,
      [dimension]: value
    }));
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setDimensionsForm(prev => ({
        ...prev,
        [dimension]: numValue
      }));
    }
  };

  const handleDimensionBlur = (dimension: 'width' | 'height') => {
    const value = inputValues[dimension];
    const numValue = parseFloat(value);
    
    if (isNaN(numValue) || numValue <= 0 || value === '') {
      setInputValues(prev => ({
        ...prev,
        [dimension]: dimensionsForm[dimension].toString()
      }));
    }
  };

  const handleApplyDimensions = () => {
    // Validate inputs before applying
    const widthValue = parseFloat(inputValues.width);
    const heightValue = parseFloat(inputValues.height);
    
    // Only apply if both values are valid numbers
    if (!isNaN(widthValue) && widthValue > 0 && !isNaN(heightValue) && heightValue > 0) {
      // Convert feet to pixels for internal storage
      const pixelWidth = Math.round(dimensionsForm.width * FEET_TO_PIXELS);
      const pixelHeight = Math.round(dimensionsForm.height * FEET_TO_PIXELS);
      
      setFloorPlan(prev => {
        // Scale all item positions proportionally to maintain their relative positions
        const scaledItems = prev.items.map(item => {
          // Get the relative position (as a percentage of the floor plan dimensions)
          const relativeX = item.position.x / prev.size.width;
          const relativeY = item.position.y / prev.size.height;
          
          // Calculate new position by applying the relative position to the new dimensions
          const newX = relativeX * pixelWidth;
          const newY = relativeY * pixelHeight;
          
          return {
            ...item,
            position: {
              x: newX,
              y: newY
            }
            // Rotation is preserved as-is since it's an angle, not a position
          };
        });
        
        return {
          ...prev,
          size: {
            width: pixelWidth,
            height: pixelHeight
          },
          items: scaledItems
        };
      });
    } else {
      // If values are invalid, reset input fields to the last valid values
      setInputValues({
        width: dimensionsForm.width.toString(),
        height: dimensionsForm.height.toString()
      });
    }
  };

  const handleDownloadPdf = async () => {
    if (!canvasRef.current) return;
    
    try {
      // Find the canvas element within the referenced div
      const canvasElement = canvasRef.current.querySelector('canvas');
      if (!canvasElement) {
        console.error('Canvas element not found');
        return;
      }
      
      // Get the viewport scroll positions
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      
      // Create canvas from the DOM element
      const canvas = await html2canvas(canvasElement, {
        scale: 2, // Higher scale for better quality
        scrollX: -scrollX,
        scrollY: -scrollY,
        windowWidth: document.documentElement.offsetWidth,
        windowHeight: document.documentElement.offsetHeight,
        useCORS: true,
        allowTaint: true,
      });
      
      // Calculate dimensions for PDF
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      
      // Calculate scale to fit the canvas in the PDF with borders
      const pdfWidth = (canvasWidth / PDF_DPI) + (BORDER_SIZE * 2 / PDF_DPI);
      const pdfHeight = (canvasHeight / PDF_DPI) + (BORDER_SIZE * 2 / PDF_DPI);
      
      // Create PDF of appropriate size
      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'pt',
        format: [pdfWidth * PDF_DPI, pdfHeight * PDF_DPI]
      });
      
      // Add the canvas image to the PDF with a 1-inch border
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', BORDER_SIZE, BORDER_SIZE, canvasWidth, canvasHeight);
      
      // Save the PDF
      pdf.save(`${floorPlan.name.replace(/\s+/g, '_')}_floor_plan.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <AppContainer>
      <MainContainer>
        <Header>
          <HeaderTop>
            <Title>{floorPlan.name}</Title>
            <ActionButtons>
              <Button onClick={handleDownloadPdf}>Download as PDF</Button>
              {isLoggedIn ? (
                <>
                  <Button onClick={handleSave}>Save Layout</Button>
                  <Button as={Link} to="/dashboard">My Layouts</Button>
                </>
              ) : (
                <>
                  <Button onClick={handleSave}>Sign in to Save</Button>
                  <Button as={Link} to="/login">Sign in to Load</Button>
                </>
              )}
              <Button onClick={handleClearAll}>Clear All</Button>
            </ActionButtons>
          </HeaderTop>
          <DimensionsForm>
            <DimensionInput>
              <Label>Length:</Label>
              <Input 
                type="text" 
                value={inputValues.width}
                onChange={(e) => handleDimensionChange('width', e.target.value)}
                onBlur={() => handleDimensionBlur('width')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDimensionBlur('width');
                    handleApplyDimensions();
                  }
                }}
              />
              <UnitLabel>ft</UnitLabel>
            </DimensionInput>
            <DimensionInput>
              <Label>Width:</Label>
              <Input 
                type="text" 
                value={inputValues.height}
                onChange={(e) => handleDimensionChange('height', e.target.value)}
                onBlur={() => handleDimensionBlur('height')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDimensionBlur('height');
                    handleApplyDimensions();
                  }
                }}
              />
              <UnitLabel>ft</UnitLabel>
            </DimensionInput>
            <ApplyButton onClick={handleApplyDimensions}>Apply Dimensions</ApplyButton>
            
            <SnapCheckboxContainer>
              <CheckboxLabel>
                <Checkbox 
                  type="checkbox" 
                  checked={snapToGrid}
                  onChange={(e) => setSnapToGrid(e.target.checked)}
                />
                Snap to Grid
              </CheckboxLabel>
            </SnapCheckboxContainer>
          </DimensionsForm>
        </Header>
        <ContentContainer>
          <Toolbar onAddItem={handleAddItem} />
          <CanvasContainer ref={canvasRef}>
            <FloorPlanCanvas
              floorPlan={floorPlan}
              onItemMove={handleItemMove}
              onItemSelect={handleItemSelect}
              selectedItemId={selectedItemId}
              useFootGrid={true}
              snapToGrid={snapToGrid}
            />
          </CanvasContainer>
          <PropertyPanel
            selectedItem={selectedItem}
            onItemChange={handleItemChange}
            onDeleteItem={handleItemDelete}
          />
        </ContentContainer>
      </MainContainer>
    </AppContainer>
  );
}

function App() {
  const { currentUser, loading } = useAuth();
  const [activePlan, setActivePlan] = useState<FloorPlan | null>(null);
  const navigate = useNavigate();

  const handleEditPlan = (plan: FloorPlan) => {
    setActivePlan(plan);
  };

  const handleSaveRequested = () => {
    if (!currentUser) {
      // Redirect to login if user tries to save without being logged in
      navigate('/login');
      return;
    }
    
    // If user is logged in, continue with save operation
    // Logic will be handled in VenuePlannerApp
  };

  const createDefaultPlan = (): FloorPlan => {
    // Default dimensions
    const defaultWidthFeet = 80; // 800px / 10 = 80ft
    const defaultHeightFeet = 60; // 600px / 10 = 60ft
    const FEET_TO_PIXELS = 10; // Same as in App.tsx

    return {
      id: uuidv4(),
      name: 'New Venue',
      size: {
        width: defaultWidthFeet * FEET_TO_PIXELS,
        height: defaultHeightFeet * FEET_TO_PIXELS
      },
      items: []
    };
  };

  // Create a direct component that doesn't use auth context
  // This will be our fallback if the Home component has issues with auth
  const DirectPlanner: React.FC = () => {
    const navigate = useNavigate();
    
    // Default dimensions
    const defaultWidthFeet = 80;
    const defaultHeightFeet = 60;
    
    const defaultPlan: FloorPlan = {
      id: uuidv4(),
      name: 'New Venue',
      size: {
        width: defaultWidthFeet * FEET_TO_PIXELS,
        height: defaultHeightFeet * FEET_TO_PIXELS
      },
      items: []
    };

    return (
      <VenuePlannerApp
        floorPlan={defaultPlan}
        onSavePlan={(plan) => {
          // Always redirect to login when saving
          navigate('/login');
        }}
        requireAuthForSave={true}
        isLoggedIn={false}
      />
    );
  };

  // If still loading auth state, show a simple loading indicator
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '20px',
        color: COLORS.primary
      }}>
        <h2>Loading Venue Planner...</h2>
      </div>
    );
  }

  return (
    <Routes>
      {/* Root route uses the new Home component */}
      <Route path="/" element={<Home />} />
      
      {/* Direct planner access with no auth dependencies */}
      <Route path="/direct" element={<DirectPlanner />} />
      
      {/* Other routes */}
      <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/" />} />
      <Route path="/signup" element={!currentUser ? <Signup /> : <Navigate to="/" />} />
      <Route path="/planner" element={<Home />} />
      
      {/* Dashboard - requires authentication */}
      <Route path="/dashboard" element={<PrivateRoute />}>
        <Route index element={<Dashboard onEditPlan={handleEditPlan} />} />
      </Route>
      
      {/* Editing a saved plan - requires authentication */}
      <Route path="/edit" element={<PrivateRoute />}>
        <Route index element={
          activePlan ? (
            <VenuePlannerApp 
              floorPlan={activePlan} 
              onSavePlan={async (plan) => {
                try {
                  // Update the existing plan in Firebase
                  await updatePlan(plan.id, plan);
                  // Return to dashboard after save
                  setActivePlan(null);
                  navigate('/dashboard');
                } catch (error) {
                  console.error('Error saving plan:', error);
                  alert('Failed to save your plan. Please try again.');
                }
              }}
              isLoggedIn={true}
            />
          ) : (
            <Navigate to="/dashboard" />
          )
        } />
      </Route>
      
      {/* Catch all route - redirect to planner */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
